/**
 * Settings & Audit. The audit log is append-only: a correction appends a
 * compensating fact, so the original entry always stays inspectable.
 */
import { useState } from 'react';
import { api } from '../lib/api';
import { useProjection } from '../hooks/useProjection';
import { Panel } from '../components/Primitives';
import { ROLE_CAPABILITIES, ROLES, WEEKLY_FUEL_CEILINGS } from '@dcc/shared';
import { useAuth } from '../lib/auth';
import { EmptyState, ErrorState, SkeletonPanel } from '../components/States';
import { absoluteTime, relativeTime } from '../lib/format';

interface AuditRow {
  id: string;
  action: string;
  actor_type: string;
  actor_id: string;
  resource_type: string;
  resource_id: string;
  reason: string | null;
  correlation_id: string | null;
  recorded_at: string;
}

export function AuditScreen() {
  const { user } = useAuth();
  const audit = useProjection(() => api.audit(100), []);
  const metrics = useProjection(() => api.metrics(), []);
  const [filter, setFilter] = useState('');

  return (
    <>
      <header className="topstrip">
        <div>
          <div className="label">Tenant policy and history</div>
          <h1 className="topstrip__title">Settings &amp; audit</h1>
        </div>
      </header>

      <Panel
        title="Roles and permissions"
        subtitle="Least privilege. Authorisation is evaluated per tenant, project and action; a role you do not hold is refused with an explanation rather than hidden."
      >
        <div className="rows">
          {ROLES.map((role) => (
            <div
              key={role}
              className="row"
              style={{ gridTemplateColumns: '160px minmax(0, 1fr)' }}
              data-tone={role === user?.role ? 'active' : undefined}
            >
              <div className="row__stack row__cell" data-label="Role">
                <strong>{role.replace('_', ' ')}</strong>
                {role === user?.role && (
                  <span className="row__sub" style={{ color: 'var(--active)' }}>
                    your role
                  </span>
                )}
              </div>
              <div className="row__cell" data-label="May do">
                <span className="small mono">
                  {ROLE_CAPABILITIES[role].includes('*')
                    ? 'everything in this tenant'
                    : ROLE_CAPABILITIES[role].join(', ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid grid--2">
        <Panel
          title="Governor policy"
          subtitle="The worker ceilings the advisory governor applies. It never acts on them on its own in this version."
        >
          <div className="rows">
            {WEEKLY_FUEL_CEILINGS.map((rule) => (
              <div
                key={rule.rule}
                className="row"
                style={{ gridTemplateColumns: 'auto minmax(0, 1fr)' }}
              >
                <span className="chip" data-tone="neutral">
                  max {rule.ceiling}
                </span>
                <span className="small row__cell" data-label="Rule">
                  {rule.rule}
                </span>
              </div>
            ))}
          </div>
          <p className="tiny dim" style={{ marginTop: 10 }}>
            Above the ceiling a worker is also refused when it has no dependency-safe READY route,
            when nobody can accept its output, or when the time it saves costs more capacity than it
            is worth.
          </p>
        </Panel>

        <Panel
          title="Lease and retention policy"
          subtitle="Exactly one captain per scope. Availability is never traded for that guarantee."
        >
          <dl
            className="small"
            style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr auto', gap: 6 }}
          >
            <dt className="dim">Default TTL</dt>
            <dd className="mono" style={{ margin: 0 }}>
              30s
            </dd>
            <dt className="dim">Renewal interval</dt>
            <dd className="mono" style={{ margin: 0 }}>
              10s (TTL / 3)
            </dd>
            <dt className="dim">Fencing</dt>
            <dd className="mono" style={{ margin: 0 }}>
              required, monotonic epoch
            </dd>
            <dt className="dim">Takeover</dt>
            <dd className="mono" style={{ margin: 0 }}>
              confirmed expiry, or privileged with a reason
            </dd>
            <dt className="dim">Split-brain</dt>
            <dd className="mono" style={{ margin: 0 }}>
              freezes dispatch until an operator clears it
            </dd>
          </dl>
          <div className="divider" />
          <div className="label">Retention</div>
          <p className="small dim" style={{ marginTop: 4 }}>
            Audit records are append-only; a correction appends a compensating fact and the original
            stays inspectable. Unlinking a connection stops ingestion and retains every project,
            event, build, deployment and audit record. Deletion is a separate authorised workflow
            and is never implied by unlink or archive.
          </p>
        </Panel>
      </div>

      <Panel
        title="Operational metrics"
        subtitle="Ingestion, deduplication, lease safety and delivery health."
      >
        {metrics.loading ? (
          <SkeletonPanel rows={2} title={false} />
        ) : metrics.error ? (
          <ErrorState error={metrics.error} onRetry={metrics.reload} />
        ) : (
          <div className="grid grid--4">
            {Object.entries(metrics.data ?? {})
              .filter(([key]) => key !== 'at' && key !== 'counters')
              .map(([key, value]) => (
                <div key={key} className="stat">
                  <span className="label">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                  <span className="stat__value">{String(value)}</span>
                </div>
              ))}
            {Object.entries((metrics.data?.counters as Record<string, number>) ?? {}).map(
              ([key, value]) => (
                <div key={key} className="stat">
                  <span className="label">{key}</span>
                  <span className="stat__value">{value}</span>
                </div>
              ),
            )}
          </div>
        )}
      </Panel>

      <Panel
        title="Audit trail"
        subtitle="Append-only. Every material mutation records its actor, resource, reason and correlation id."
        actions={
          <label className="inline inline--tight small">
            <span className="sr-only">Filter audit entries</span>
            <input
              className="btn btn--sm"
              style={{ justifyContent: 'flex-start', minWidth: 180 }}
              type="search"
              placeholder="Filter by action or resource"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </label>
        }
        flush
      >
        {audit.loading ? (
          <SkeletonPanel rows={5} title={false} />
        ) : audit.error ? (
          <ErrorState error={audit.error} onRetry={audit.reload} />
        ) : (
          <div className="rows">
            {(() => {
              const rows = (audit.data?.audit ?? []) as unknown as AuditRow[];
              const filtered = rows.filter(
                (row) =>
                  filter.length === 0 ||
                  row.action.includes(filter) ||
                  row.resource_type.includes(filter) ||
                  (row.reason ?? '').toLowerCase().includes(filter.toLowerCase()),
              );
              if (filtered.length === 0) {
                return (
                  <div style={{ padding: 16 }}>
                    <EmptyState
                      title="No audit entries match"
                      body={
                        filter
                          ? 'Try a different action or resource.'
                          : 'Nothing has been recorded yet.'
                      }
                    />
                  </div>
                );
              }
              return filtered.map((row) => (
                <div
                  key={row.id}
                  className="row"
                  style={{ gridTemplateColumns: '1.1fr 1.2fr 1fr auto' }}
                >
                  <div className="row__stack row__cell" data-label="Action">
                    <strong className="mono">{row.action}</strong>
                    <span className="row__sub">
                      {row.actor_type}:{row.actor_id}
                    </span>
                  </div>
                  <div className="row__stack row__cell" data-label="Resource">
                    <span className="mono truncate">{row.resource_type}</span>
                    <span className="row__sub mono truncate">{row.resource_id}</span>
                  </div>
                  <div className="row__cell" data-label="Reason">
                    <span className="small">{row.reason ?? '—'}</span>
                  </div>
                  <div
                    className="row__stack row__cell"
                    data-label="When"
                    style={{ alignItems: 'flex-end' }}
                  >
                    <span className="small" title={absoluteTime(row.recorded_at)}>
                      {relativeTime(row.recorded_at)}
                    </span>
                    <span className="row__sub mono">{row.correlation_id ?? ''}</span>
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </Panel>
    </>
  );
}
